export interface RemittanceLine {
  amount: number | null;
  values: Record<string, string>;
}

export interface DeductionCreateFormSubmitProps {
  id?: string;
  isAssigned?: boolean;
  defendant: { id?: string; label?: string };
  type: string;
  amount: string;
  remittanceLines?: RemittanceLine[];
  vendorAccount?: {
    id?: string;
    label?: string;
    defaultPaymentMethod?: string;
    achAccounts?: {
      id: number | string;
      nickname: string;
      accountType: string;
      routingNumber?: string;
      maskedAccountNumber?: string;
      accountNumberLast4?: string;
      isDefault: boolean;
    }[];
    checkAccounts?: {
      id: number | string;
      nickname: string;
      payeeName?: string;
      recipientName?: string;
      address: string;
      addressLine2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      isDefault: boolean;
    }[];
    accounting?: { isSynced?: boolean; externalVendorId?: string | null } | null;
  } | null;
  description?: string;
  memo?: string;
  uploadInvoice?: File | string | null;
  disposition?: string;
  paymentMethod?: string;
  paymentAccount?: { id?: string; label?: string } | null;
  externalPaymentReference?: string | null;
  // externalPaymentMemo?: string;
  externalPaymentDate?: Date | null;
}

export interface SettlementCreateFormSubmitProps {
  id?: string;
  settlementType?: string;
  defendant: { id?: string; label?: string };
  amount?: number;
  pipAmount?: number;
  lienReimbursementAmount?: number;
  autoFillMemo?: boolean;
  uploadedMemo?: File | null;
}

export interface DeductionEditFormSubmitProps {
  id?: string;
  caseReferenceId?: string;
  referenceId?: string;
  caseSystemName?: string;
  firm?: string;
  isAssigned?: boolean;
  defendant: { id?: string; label?: string };
  type: string;
  amount: string;
  remittanceLines?: RemittanceLine[];
  vendorAccount?: {
    id?: string;
    label?: string;
    defaultPaymentMethod?: string;
    achAccounts?: {
      id: number | string;
      nickname: string;
      accountType: string;
      routingNumber?: string;
      maskedAccountNumber?: string;
      accountNumberLast4?: string;
      isDefault: boolean;
    }[];
    checkAccounts?: {
      id: number | string;
      nickname: string;
      payeeName?: string;
      recipientName?: string;
      address: string;
      addressLine2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      isDefault: boolean;
    }[];
    accounting?: { isSynced?: boolean; externalVendorId?: string | null } | null;
  } | null;
  description?: string | null;
  memo?: string | null;
  uploadInvoice?: File | string | null;
  disposition?: string | null;
  paymentMethod?: string | null;
  paymentAccount?: { id?: string; label?: string } | null;
  preSettlementAmount?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entryDate?: any;
  externalPaymentReference?: string | null;
  externalPaymentMemo?: string | null;
  externalPaymentDate?: Date | null;
}

export interface NetSettlementEditFormSubmitProps {
  paymentIntention: string;
  externalPaymentMethod?: string | null;
  externalPaymentReference?: string | null;
  externalPaymentMemo?: string | null;
  externalPaymentDate?: Date | null;
}

export interface AttorneyFeeEditPopupFormSubmitProps {
  disposition: string;
  paymentMethod?: string | null;
  externalPaymentReference?: string | null;
  externalPaymentMemo?: string | null;
  externalPaymentDate?: Date | null;
}

export interface AttorneyFeeEditFormSubmitProps {
  id?: string;
  name: string;
  fee: number;
  role: string;
}

export interface DeductionDraftEditFormSubmitProps {
  id?: number;
  vendorName?:
    | {
        label?: string;
        id?: string;
        defaultPaymentMethod?: string;
        accounting?: { isSynced?: boolean; externalVendorId?: string | null } | null;
      }
    | undefined;
  amount: number;
  vendorParticipationId?: string | null;
  type: string;
  description?: string | null;
  paymentMethod?: string | null;
  paymentAccount?: { id?: string; label?: string } | null;
  matchingMode?: string | null;
  // memo?: string | null;
  // disposition?: string | null;
  // paymentMethod?: string | null;
}

export interface SettlementDeductionEditFormSubmitProps {
  id?: string;
  caseReferenceId?: string;
  caseSystemName?: string;
  firm?: string;
  isAssigned?: boolean;
  defendant: { id?: string; label?: string };
  type: string;
  amount: string;
  vendorName?: {
    id?: string;
    label?: string;
    defaultPaymentMethod?: string;
    accounting?: { isSynced?: boolean; externalVendorId?: string | null };
  } | null;
  description?: string | null;
  memo?: string | null;
  uploadInvoice?: File | string | null;
  disposition?: string | null;
  paymentMethod?: string | null;
}

export interface SettlementEditFormSubmitProps {
  id?: string;
  isAssigned?: boolean;
  caseReferenceId?: string;
  defendant: { id?: string; label?: string };
}

export enum MenuOption {
  Edit = 'Edit',
  Download = 'Download Invoice',
}

export enum MenuOptionDetails {
  Edit = 'Edit',
  Download = 'Download Invoice',
}

export enum DeductionMenuOption {
  MarkAsRecovery = 'Mark as Recovery',
}

export interface SettlementDraft {
  id: number;
  case: {
    id: number;
    referenceId: string;
    name: string;
  };
  client: {
    id: number;
    firstName: string;
    middleName: string | null;
    lastName: string;
  };
  defendant: {
    id: number;
    name: string;
  };
  settledAmount: number;
  pipAmount?: number;
  lienReimbursementAmount?: number;
  settlementType?: 'STANDARD' | 'STANDARD_PIP' | 'LIEN_REPAYMENT';
  status: string;
  deductions: Array<{
    id: number;
    type: string;
    amount: number;
    vendorParticipationId: number | null;
    vendorName: string | null;
    vendorSuggestions: Array<{
      id: number;
      vendorName: string;
      similarity: number;
      paymentMethod: string;
      provisioning: {
        ach: { isProvisioned: boolean };
        check: { isProvisioned: boolean };
      };
      accounting?: {
        isSynced: boolean;
        externalVendorId: string;
        link: string | null;
        lastSyncedAt: string | null;
        status: string | null;
      };
    }>;
    paymentMethod: string;
    vendorPaymentAccountId?: number | null;
    vendorPaymentAccountName?: string | null;
    disposition: string;
    matchingMode: string | null;
    memo: string;
    originalText: string;
    description: string;
    provisioning: {
      ach: { isProvisioned: boolean };
      check: { isProvisioned: boolean };
    };
    accounting?: {
      isSynced: boolean;
      externalVendorId: string;
      link: string | null;
      lastSyncedAt: string | null;
      status: string | null;
    };
  }>;
  attorneysFees: {
    total: number;
    feePrecentage: number;
    leadFirm: {
      id: number;
      name: string;
      fee: number;
      role: string;
      disposition: string;
      paymentMethod: string;
      accounting?: {
        isSynced: boolean;
        externalVendorId: string;
        link: string | null;
        lastSyncedAt: string | null;
        status: string | null;
      };
    };
    secondaryFirms: Array<{
      id: number;
      name: string;
      fee: number;
      role: string;
      disposition: string;
      paymentMethod: string;
      accounting?: {
        isSynced: boolean;
        externalVendorId: string;
        link: string | null;
        lastSyncedAt: string | null;
        status: string | null;
      };
    }>;
  };
  netSettlement: {
    id: number;
    clientId: number;
    amount: number;
    name: string;
    accounting?: {
      isSynced: boolean;
      externalVendorId: string;
      link: string | null;
      lastSyncedAt: string | null;
      status: string | null;
    };
  };
  filename: string;
  docpath: string;
  fileHash: string;
  warningMessages: string[];
  isAccountingIntegrated: boolean;
}

export type AttorneyFeeDisposition = 'Pay' | 'Withhold' | 'External';

export interface UpdateAttorneyFeeDispositionRequest {
  counselEntityType: 'FIRM' | 'VENDOR';
  /** Only for FIRM */
  firmId?: number;
  /** Only for VENDOR (id returned by counsel-entities) */
  vendorFirmParticipationId?: number;
  disposition: AttorneyFeeDisposition;
  externalPaymentMethod?: string | null;
  externalPaymentReference?: string | null;
  externalPaymentMemo?: string | null;
  externalPaymentDate?: string | null;
}
